import type { CustomPlacement } from '../types';

export interface SnapSize { width: number; height: number }
export interface SnapRect extends SnapSize { x: number; y: number }
export interface SnapGuides { x: number[]; y: number[] }
type Anchor = 0 | 0.5 | 1;
type ScaleCandidate = { id: string; scale: number; distance: number; pixelsPerScale: number };

const CAPTURE_PX = 4;
const RELEASE_PX = 8;
const anchors: Anchor[] = [0, 0.5, 1];
type SnapEditor = 'dynamicZoom' | 'custom';
// Editing preferences only. Re-opening/rebinding a modal keeps the choice;
// a fresh page starts with both enabled. They never enter template/export data.
const enabledByEditor: Record<SnapEditor, boolean> = { dynamicZoom: true, custom: true };
export function getEditorSnapsEnabled(editor: SnapEditor): boolean { return enabledByEditor[editor]; }
export function setEditorSnapsEnabled(editor: SnapEditor, enabled: boolean): void { enabledByEditor[editor] = enabled; }
export function renderSnapsButton(id: string, editor: SnapEditor): string {
  return `<button id="${id}" class="editor-snaps-toggle" type="button" aria-pressed="${getEditorSnapsEnabled(editor)}" title="Snap to alignment guides"><span aria-hidden="true">⌖</span> Snaps</button>`;
}

/** A gesture-local magnet. Always feed raw gesture coordinates, never the
 * previously snapped output, so crossing the release radius remains possible. */
export class EditorSnapSession {
  private x: Anchor | null = null;
  private y: Anchor | null = null;
  private scaleId: string | null = null;
  private previousScale: number | null = null;

  reset(): void { this.x = this.y = null; this.scaleId = null; this.previousScale = null; }
  beginScale(scale: number): void { this.reset(); this.previousScale = scale; }

  move(rect: SnapRect, bounds: SnapSize, centerOnly = false): { rect: SnapRect; guides: SnapGuides } {
    const choices = centerOnly ? [0.5] as Anchor[] : anchors;
    const x = this.snapAxis(rect.x, rect.width, bounds.width, this.x, choices);
    const y = this.snapAxis(rect.y, rect.height, bounds.height, this.y, choices);
    this.x = x.anchor; this.y = y.anchor;
    const next = { ...rect, x: x.position, y: y.position };
    return { rect: next, guides: getAlignedGuides(next, bounds, choices) };
  }

  private snapAxis(position: number, size: number, bound: number, held: Anchor | null, choices: Anchor[]) {
    const correction = (anchor: Anchor) => anchor * bound - (position + anchor * size);
    if (held !== null && Math.abs(correction(held)) <= RELEASE_PX) {
      return { position: position + correction(held), anchor: held };
    }
    const nearest = choices.filter(anchor => Math.abs(correction(anchor)) <= CAPTURE_PX)
      .sort((a, b) => Math.abs(correction(a)) - Math.abs(correction(b)))[0];
    return nearest === undefined ? { position, anchor: null } : { position: position + correction(nearest), anchor: nearest };
  }

  scale(placement: CustomPlacement, source: SnapSize, bounds: SnapSize, minScale = 0.05, maxScale = 6) {
    const raw = { ...placement, scale: Math.max(minScale, Math.min(maxScale, placement.scale)) };
    const rect = getPlacementSnapRect(raw, source, bounds);
    const center = { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 };
    const candidates: ScaleCandidate[] = [];
    for (const axis of ['x', 'y'] as const) {
      const size = axis === 'x' ? rect.width : rect.height;
      const bound = axis === 'x' ? bounds.width : bounds.height;
      const pixelsPerScale = size / raw.scale / 2;
      for (const edge of [0, 1]) {
        const scale = (edge === 0 ? center[axis] : bound - center[axis]) / pixelsPerScale;
        if (Number.isFinite(scale) && scale >= minScale && scale <= maxScale) candidates.push({
          id: `${axis}:${edge}`, scale, pixelsPerScale, distance: Math.abs(scale - raw.scale) * pixelsPerScale
        });
      }
    }
    const held = candidates.find(candidate => candidate.id === this.scaleId);
    const releasedId = held && held.distance > RELEASE_PX ? held.id : null;
    const crossed = (candidate: ScaleCandidate) => this.previousScale !== null &&
      Math.abs(raw.scale - this.previousScale) * candidate.pixelsPerScale <= 48 &&
      (this.previousScale - candidate.scale) * (raw.scale - candidate.scale) < 0;
    const chosen = held && held.distance <= RELEASE_PX ? held : candidates
      .filter(candidate => !(releasedId && held && Math.abs(candidate.scale - held.scale) < 1e-8) && (candidate.distance <= CAPTURE_PX || crossed(candidate)))
      .sort((a, b) => a.distance - b.distance)[0];
    this.scaleId = chosen?.id ?? null;
    this.previousScale = raw.scale;
    const next = { ...raw, scale: chosen?.scale ?? raw.scale };
    return { placement: next, guides: getAlignedGuides(getPlacementSnapRect(next, source, bounds), bounds) };
  }
}

export function getPlacementSnapRect(placement: CustomPlacement, source: SnapSize, bounds: SnapSize): SnapRect {
  const cover = Math.max(bounds.width / Math.max(1, source.width), bounds.height / Math.max(1, source.height));
  const width = source.width * cover * placement.scale, height = source.height * cover * placement.scale;
  return { x: bounds.width * (0.5 + placement.offsetX) - width / 2,
    y: bounds.height * (0.5 + placement.offsetY) - height / 2, width, height };
}

export function snapPlacementMove(session: EditorSnapSession, placement: CustomPlacement, source: SnapSize, bounds: SnapSize) {
  const result = session.move(getPlacementSnapRect(placement, source, bounds), bounds);
  return { guides: result.guides, placement: { ...placement,
    offsetX: (result.rect.x + result.rect.width / 2) / bounds.width - 0.5,
    offsetY: (result.rect.y + result.rect.height / 2) / bounds.height - 0.5 } };
}

function getAlignedGuides(rect: SnapRect, bounds: SnapSize, choices = anchors): SnapGuides {
  return {
    x: choices.filter(a => Math.abs(rect.x + a * rect.width - a * bounds.width) < 0.25),
    y: choices.filter(a => Math.abs(rect.y + a * rect.height - a * bounds.height) < 0.25)
  };
}

export function renderSnapGuides(centerOnly = false): string {
  return `<div class="editor-snap-guides" data-editor-snap-guides aria-hidden="true" hidden>
    ${(['x', 'y'] as const).flatMap(axis => (centerOnly ? [0.5] : anchors).map(at =>
      `<i class="editor-snap-line ${axis} ${at === 0.5 ? 'is-center' : 'is-edge'}" data-snap-axis="${axis}" data-snap-at="${at}" style="--snap-position:${at * 100}%"></i>`)).join('')}
    <i class="editor-snap-center"></i></div>`;
}

export function paintSnapGuides(host: HTMLElement | null, guides: SnapGuides | null, showCenters = false): void {
  if (!host) return;
  host.hidden = !guides && !showCenters;
  host.classList.toggle('show-centers', showCenters);
  host.classList.toggle('is-centered', Boolean(guides?.x.includes(0.5) && guides?.y.includes(0.5)));
  for (const line of host.querySelectorAll<HTMLElement>('[data-snap-axis]')) {
    const axis = line.dataset.snapAxis as 'x' | 'y';
    line.classList.toggle('active', Boolean(guides?.[axis].includes(Number(line.dataset.snapAt))));
  }
}
